import { v4 as uuidv4 } from 'uuid';

/**
 * Converts a HEIC blob (or File) to a JPEG blob.
 *
 * @param {Blob} blob - The HEIC image data as a Blob or File.
 * @returns {Promise<Blob>} - A promise that resolves with the converted JPEG data as a Blob.
 */
export async function convertHeicToJpeg(blob: Blob): Promise<Blob> {
  const { heicTo } = await import('heic-to');

  const result = await heicTo({
    blob,
    type: 'image/jpeg',
    quality: 0.5,
  });

  return result;
}

/**
 * Generates a unique filename for an uploaded file.
 *
 * @param {string} originalFilename - The original filename (e.g., "photo.jpg")
 * @returns {string} - The generated unique filename.
 */
export function generateUniqueFilename(originalFilename: string): string {
  const { fileName, extension } = getFilenameAndExtension(originalFilename);
  return `${fileName}-${uuidv4()}${extension}`;
}

/**
 * Extracts the filename and extension from a file object or a filename string,
 * always returning an object with `fileName` and `extension`.
 *
 * @param fileOrFilename A file, or an object with a `name` property, or a simple string
 */
export function getFilenameAndExtension(fileOrFilename: File | { name: string } | string): { fileName: string; extension: string } {
  const fullName = typeof fileOrFilename === 'string' ? fileOrFilename : fileOrFilename.name;

  const lastDot = fullName.lastIndexOf('.');

  if (lastDot === -1) {
    return { fileName: fullName, extension: '' };
  }

  return {
    fileName: fullName.substring(0, lastDot),
    extension: fullName.substring(lastDot),
  };
}
